import React, { useEffect, useState } from 'react';
import { XIcon, LoaderIcon } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
export function YtMartesRegistrationModal({
  isOpen,
  onClose,
  onSuccess
}: RegistrationModalProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    phone: '',
    countryCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Track modal open
  useEffect(() => {
    if (isOpen) {
      trackEvent('modal_opened');
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Validate country code is selected
    if (!formData.countryCode) {
      setError('Por favor selecciona un código de país');
      return;
    }
    // Validate phone number is entered
    if (!formData.phone) {
      setError('Por favor ingresa tu número de teléfono');
      return;
    }
    setIsSubmitting(true);
    try {
      // Combine country code with phone number
      const fullPhoneNumber = `${formData.countryCode}${formData.phone}`;
      const response = await fetch('https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/92d4e807-a7cf-4651-bb94-a37676bbb29e', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombreCompleto: formData.nombre,
          email: formData.email,
          phone: fullPhoneNumber
        })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      // Track successful submission
      trackEvent('modal_submitted');
      onSuccess();
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Hubo un error al enviar el formulario. Por favor, intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4 overflow-y-auto" data-id="element-1106">
      <div className="bg-gray-900 rounded-lg max-w-md w-full relative z-[101] my-8" data-id="element-1107">
        <div className="bg-gray-900 border-b border-gray-700 p-4 md:p-6 flex justify-between items-center rounded-t-lg" data-id="element-1108">
          <h2 className="text-xl md:text-2xl font-bold text-white" data-id="element-1109">
            Reserva Tu Lugar
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 -mr-2 touch-manipulation" aria-label="Close" disabled={isSubmitting} data-id="element-1110">
            <XIcon className="h-6 w-6" data-id="element-1111" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4" data-id="element-1112">
          <div data-id="element-1113">
            <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1114">
              Nombre *
            </label>
            <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} required minLength={2} placeholder="Tu nombre" className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base focus:outline-none focus:border-red-500 touch-manipulation" disabled={isSubmitting} data-id="element-1115" />
          </div>
          <div data-id="element-1116">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1117">
              Email *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="tu@email.com" className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base focus:outline-none focus:border-red-500 touch-manipulation" disabled={isSubmitting} data-id="element-1118" />
          </div>
          <div data-id="element-1119">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1120">
              Teléfono *
            </label>
            <div className="flex gap-2" data-id="element-1121">
              <select id="countryCode" name="countryCode" value={formData.countryCode} onChange={handleChange} required className="w-32 px-3 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base focus:outline-none focus:border-red-500 touch-manipulation" disabled={isSubmitting} data-id="element-1122">
                <option value="" data-id="element-1123">Código</option>
                <option value="+52" data-id="element-1124">🇲🇽 +52</option>
                <option value="+1" data-id="element-1125">🇺🇸 +1</option>
                <option value="+1" data-id="element-1126">🇨🇦 +1</option>
                <option value="+34" data-id="element-1127">🇪🇸 +34</option>
                <option value="+54" data-id="element-1128">🇦🇷 +54</option>
                <option value="+56" data-id="element-1129">🇨🇱 +56</option>
                <option value="+57" data-id="element-1130">🇨🇴 +57</option>
                <option value="+51" data-id="element-1131">🇵🇪 +51</option>
                <option value="+58" data-id="element-1132">🇻🇪 +58</option>
                <option value="+593" data-id="element-1133">🇪🇨 +593</option>
                <option value="+591" data-id="element-1134">🇧🇴 +591</option>
                <option value="+595" data-id="element-1135">🇵🇾 +595</option>
                <option value="+598" data-id="element-1136">🇺🇾 +598</option>
                <option value="+507" data-id="element-1137">🇵🇦 +507</option>
                <option value="+506" data-id="element-1138">🇨🇷 +506</option>
                <option value="+503" data-id="element-1139">🇸🇻 +503</option>
                <option value="+502" data-id="element-1140">🇬🇹 +502</option>
                <option value="+504" data-id="element-1141">🇭🇳 +504</option>
                <option value="+505" data-id="element-1142">🇳🇮 +505</option>
              </select>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Número de teléfono" className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base focus:outline-none focus:border-red-500 touch-manipulation" disabled={isSubmitting} data-id="element-1143" />
            </div>
          </div>
          {error && <div className="text-red-500 text-sm bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3" data-id="element-1144">
              {error}
            </div>}
          <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-4 px-6 shadow-lg transform hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center touch-manipulation" data-id="element-1145">
            {isSubmitting ? <>
                <LoaderIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-1146" />
                Enviando...
              </> : 'RESERVAR MI LUGAR'}
          </button>
        </form>
      </div>
    </div>;
}